const LS_LOCATIONS = 'gd:shipping-locations';
const LS_DESTINATION = 'gd:shipping-destination';
const LS_TAB = 'gd:shipping-tab';
const LOCATIONS_VERSION = 4; // incrementar cuando cambia el shape O LOS DATOS del endpoint

// GALPÓN (ago 2026) — acceso a localStorage a prueba de `SecurityError`.
// El ACCESO A LA PROPIEDAD `window.localStorage` puede LANZAR (DOMException 18) cuando el
// navegador deniega el almacenamiento: cookies de terceros bloqueadas, privacidad estricta,
// WebView, iframe sandboxeado. NO alcanza con envolver el JSON.parse.
// CASO REAL (Sentry SHOPIFY-GALPON-1S, 20-ago-2026): la excepción en `#restoreTab()` cortaba
// `#init()` ANTES de `#bindEvents()` → el selector de envío quedaba MUERTO y, en el cart, el
// botón PAGAR sin sincronizar. Un cliente con storage bloqueado no podía cotizar ni comprar.
// ⚠️ NO volver a tocar localStorage directo en este archivo: usar SIEMPRE estos helpers.
// Sin storage el selector funciona igual, solo no recuerda la comuna entre visitas.
const lsGet = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { window.localStorage.setItem(k, v); return true; } catch { return false; } };

// GALPÓN (7-sep-2026) — `AbortSignal.timeout()` existe desde Safari 16 / Chrome 103. En iOS ≤15
// (Instagram in-app en iPhones viejos, visto en Sentry el 7-sep: iOS 15.6.1) es `undefined` y
// llamarlo LANZA TypeError dentro de #fetchQuote → caía en el catch → "Error al calcular envío"
// en TODOS los intentos. Un cliente con iOS 15 nunca podía cotizar. Fallback con AbortController.
const timeoutSignal = (ms) => {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms);
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
};
// El fallback aborta con `AbortError`; el nativo con `TimeoutError`. Los dos son "se acabó el tiempo".
const esTimeout = (err) => err?.name === 'TimeoutError' || err?.name === 'AbortError';

// GALPÓN (20-sep-2026) — ENCADENADO DE FOCO + AUTOSCROLL A PAGAR.
// POR QUÉ: medido en el S23 de F dentro del in-app de Instagram, entre "agregar al carro" y
// VER el botón PAGAR había 6 pasos y 4 scrolls manuales (región en la posición 13 de 16,
// comuna con 52 opciones, y tras cotizar el botón seguía fuera de viewport). GA4 1-20 sep:
// 214 carritos → 23 checkouts (se pierde el 89%; en Paid Social solo cruza el 7,5%).
// El selector es OBLIGATORIO (es el mecanismo de control del checkout, ver #checkoutWithShipping)
// → no se quita ni se hace opcional: se le saca la fricción de NAVEGACIÓN. Pedido textual de F:
// "después de seleccionar región, foco en comuna; si aplica, foco en localidad; y recién ahí
// autoscrollear al final para dejar visible el botón pagar".
//
// Regla: el encadenado corre SOLO ante un cambio hecho por el usuario con puntero/touch.
//  - NUNCA en la restauración desde localStorage (robaría el foco al cargar la página).
//  - NUNCA si el cambio vino del teclado: en Chrome cada flecha sobre un <select> cerrado
//    dispara `change` → mover el foco ahí le quitaría al usuario la posibilidad de seguir
//    recorriendo las regiones. Quien navega con teclado sigue con Tab, como siempre.
//
// `showPicker()` sobre <select>: Chrome/Android/WebView 121+, Firefox 122+. Safari/iOS NO lo
// tiene (solo detrás de flag, BCD 20-sep-2026) → ahí queda el foco solo. Requiere activación
// de usuario y lanza si no la hay o si el elemento no está renderizado → siempre en try/catch.
// Apagar acá si a F le resulta intrusivo que el desplegable siguiente se abra solo.
const ABRIR_SIGUIENTE_SELECT = true;
// El autoscroll a PAGAR se ejecuta cuando la cotización TERMINA de renderizar (la tarifa llega
// async del connector). Si el connector tarda más que esto, no se scrollea: el cliente ya
// pudo haberse movido solo y un salto tardío se siente como que la página se mueve sola.
const REVELAR_CHECKOUT_VENTANA_MS = 4000;

// GALPÓN (1-sep-2026) — instrumentación del selector hacia GA4.
// POR QUÉ EXISTE: el selector es el paso MÁS decisivo del embudo (sin zona el botón PAGAR
// queda bloqueado) y hasta hoy era un punto ciego total en analytics — cero eventos. El dato
// medido que lo motivó: dom 30-ago, 52 carritos → 4 checkouts → 0 compras. GA4 solo recibe
// los eventos estándar que manda Shopify (view_item, add_to_cart, begin_checkout, purchase);
// nada de lo que pasa ACÁ ADENTRO llegaba. Sin esto no se puede distinguir entre "no vio el
// selector", "el flete le pareció caro" y "no hay cobertura en su comuna" — tres problemas
// con tres soluciones distintas.
//
// gtag() ya está en la página (GA4 G-QVF72T6XKD, vía el canal de ventas de Google de Shopify),
// verificado en producción 1-sep-2026 → no hace falta GTM ni ninguna librería nueva.
//
// ⚠️ NUNCA dejar que la medición rompa la venta: si gtag no existe (bloqueador, consentimiento
// denegado, script caído) esto es un no-op silencioso. Mismo criterio que lsGet/lsSet. El
// try/catch NO es decorativo — un throw acá cortaría el flujo de compra, que es justo lo que
// este archivo ya sufrió una vez con localStorage (ver Sentry SHOPIFY-GALPON-1S arriba).
const track = (event, params = {}) => {
  try {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', event, params);
  } catch { /* la medición jamás bloquea la compra */ }
};

// Meta: solo para los hechos que sirven para ARMAR PÚBLICOS de retargeting, no para
// analizar. "Cotizó el envío y no compró" y "quiso pagar y el sitio lo frenó" son públicos
// muchísimo más calientes que "visitó el sitio", que es lo único que tenemos hoy.
// ⚠️ `trackCustom`, NO `track`: con un nombre propio, `track` hace que Meta lo descarte
// en silencio. Los eventos de diagnóstico (errores, sin cobertura) NO van acá: no son
// públicos accionables, solo ruido en el administrador de eventos.
const meta = (event, params = {}) => {
  try {
    if (typeof window.fbq !== 'function') return;
    window.fbq('trackCustom', event, params);
  } catch { /* noop */ }
};

class GdShippingSelector extends HTMLElement {
  #regions = [];
  #items = [];
  #state = { regionCode: '', communeName: '', localityCode: '', localityLabel: '' };
  #connectorBase = '';
  #storefrontToken = '';
  #pickupLocationGid = '';
  #activeTab = 'shipping'; // 'shipping' | 'pickup'
  #abortController = null;
  #mode = 'cart'; // 'cart' (tabs + bloqueo checkout + pre-fill) | 'pdp' (solo cotizar)
  #viaTeclado = false;          // el último gesto sobre un select fue del teclado → no encadenar
  #revelarCheckoutHasta = 0;    // timestamp límite para autoscrollear a PAGAR tras cotizar (0 = no)

  connectedCallback() {
    // Horizon puede montar el custom element antes de que sus hijos estén en el DOM.
    // Esperamos hasta que el select de región exista antes de inicializar.
    this.#waitForChildren(() => this.#init());
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    // Al desconectarse se abortaron los listeners → si el nodo se vuelve a conectar hay que
    // re-inicializar de verdad. Liberar el guard acá (y NO en `connectedCallback`) es lo que
    // impide el loop: reconectar sin haberse desconectado no re-ejecuta `#init()`.
    this.#yaInicializado = false;
  }

  // EL CALLBACK DEBE CORRER UNA SOLA VEZ por conexión. Hay DOS caminos que pueden invocarlo
  // (el observer y el setTimeout de rescate a los 3s) y sin guard corrían ambos.
  // El guard es de INSTANCIA y se libera en `disconnectedCallback` — donde se abortan los
  // listeners—, así que un nodo que se reconecta sí vuelve a inicializarse de verdad.
  // Medido en producción 1-sep-2026 (vía dataLayer): `#init()` corre 1 vez por carga. ✅
  #yaInicializado = false;

  #waitForChildren(cb) {
    const unaVez = () => { if (this.#yaInicializado) return; this.#yaInicializado = true; cb(); };
    if (this.querySelector('[data-gd-select="region"]')) { unaVez(); return; }
    const obs = new MutationObserver(() => {
      if (this.querySelector('[data-gd-select="region"]')) { obs.disconnect(); unaVez(); }
    });
    obs.observe(this, { childList: true, subtree: true });
    setTimeout(() => { obs.disconnect(); if (this.querySelector('[data-gd-select="region"]')) unaVez(); }, 3000);
  }

  #init() {
    if (!this.isConnected) return;

    this.#mode = this.dataset.mode === 'pdp' ? 'pdp' : 'cart';
    this.#connectorBase = this.dataset.connectorBase || '';
    this.#storefrontToken = this.dataset.storefrontToken || '';
    this.#pickupLocationGid = this.dataset.pickupLocationGid || '';
    if (!this.#connectorBase) {
      console.error('[gd-shipping] CONNECTOR_BASE no configurado — ir a Admin → Personalizar tema → Galpón Connector');
      this.#setResult('error', 'Calculadora de envío no configurada. Contacta al administrador.');
      return;
    }

    try {
      this.#items = JSON.parse(decodeURIComponent(this.dataset.items || '[]'));
    } catch {
      this.#items = [];
    }

    // Total del carro para la instrumentación, desde Liquid (ver #cartTotalPesos).
    const totalAttr = parseInt(this.dataset.cartTotal || '0', 10);
    if (Number.isFinite(totalAttr) && totalAttr > 0) this.#cartTotalCache = totalAttr;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    this.#restoreDestination();
    this.#restoreTab();
    this.#bindEvents();
    this.#openByDefault();
    this.#syncCheckoutButton(); // estado inicial del botón PAGAR (bloqueado si falta zona)
    this.#loadLocations();
  }

  // Cuándo abrir el disclosure al montar:
  //  - CART: SIEMPRE. Elegir despacho es parte del flujo de compra y es OBLIGATORIO antes
  //    del checkout → tenerlo abierto empuja a completarlo (menos clicks bloqueados).
  //  - PDP: solo si ya hay destino guardado (el cliente viene a ver la pieza, no a cotizar).
  #openByDefault() {
    const hasDest = this.#state.regionCode && this.#state.communeName;
    const open = this.#mode === 'cart' ? true : !!hasDest;

    // EVENTO 1 — el selector se MOSTRÓ, y con qué estado arranca el cliente.
    // OJO al interpretarlo: en cart el panel se abre SIEMPRE, así que esto NO mide
    // "cuántos lo abrieron" sino cuántos llegaron con zona ya guardada vs. cuántos
    // tienen que elegirla ahí. Los `zona_guardada:false` en cart son exactamente la
    // población que puede toparse con el botón PAGAR bloqueado.
    if (this.#mode === 'cart') {
      track('gd_shipping_selector_view', {
        contexto: this.#mode,
        zona_guardada: !!hasDest,
        comuna: this.#state.communeName || '(sin zona)',
      });
    }

    if (!open) return;
    const details = this.querySelector('[data-gd-details]');
    if (details) details.open = true;
  }

  // ── Tabs ──────────────────────────────────────────────────────────────────

  #restoreTab() {
    // En PDP no hay tabs (envío es lo único; retiro es gratis y se sobreentiende).
    if (this.#mode === 'pdp') { this.#activeTab = 'shipping'; return; }
    const saved = lsGet(LS_TAB);
    // Solo restaurar pickup si no hay destino guardado. Si hay destino, el usuario
    // probablemente volvió atrás desde el checkout habiendo ingresado una dirección.
    const hasDestination = !!(this.#state.regionCode || this.#state.communeName);
    if (saved === 'pickup' && !hasDestination) this.#activeTab = 'pickup';
    this.#applyTab(this.#activeTab);
  }

  #applyTab(tab) {
    this.#activeTab = tab;
    const tabShipping = this.querySelector('[data-gd-tab="shipping"]');
    const tabPickup = this.querySelector('[data-gd-tab="pickup"]');
    const panelShipping = this.querySelector('[data-gd-panel="shipping"]');
    const panelPickup = this.querySelector('[data-gd-panel="pickup"]');
    if (!tabShipping || !tabPickup || !panelShipping || !panelPickup) return;

    const isShipping = tab === 'shipping';
    tabShipping.setAttribute('aria-selected', isShipping ? 'true' : 'false');
    tabPickup.setAttribute('aria-selected', isShipping ? 'false' : 'true');
    tabShipping.classList.toggle('gd-shipping-selector__tab--active', isShipping);
    tabPickup.classList.toggle('gd-shipping-selector__tab--active', !isShipping);
    panelShipping.hidden = !isShipping;
    panelPickup.hidden = isShipping;
    this.#syncCheckoutButton(); // pickup desbloquea; volver a envío sin zona re-bloquea
  }

  #bindTabs() {
    const signal = this.#abortController.signal;
    this.querySelector('[data-gd-tab="shipping"]')?.addEventListener('click', () => {
      this.#applyTab('shipping');
      lsSet(LS_TAB, 'shipping');
    }, { signal });
    this.querySelector('[data-gd-tab="pickup"]')?.addEventListener('click', () => {
      this.#applyTab('pickup');
      lsSet(LS_TAB, 'pickup');
      // NO borramos el destino al pasar a retiro: el cliente que tantea "Retiro" y vuelve
      // a "Envío" conserva su comuna. El retiro lo marca cartBuyerIdentityUpdate(PICK_UP)
      // en el checkout, no el borrado de dirección (eso era un workaround viejo de la época
      // de los parámetros por URL, ya innecesario con la Storefront API). Y el paso a envío
      // limpia el pickup explícito en #checkoutWithShipping. Ver decisión en el harness.
    }, { signal });
  }

  // ── Selects ──────────────────────────────────────────────────────────────

  get #regionSelect() { return this.querySelector('[data-gd-select="region"]'); }
  get #communeSelect() { return this.querySelector('[data-gd-select="commune"]'); }
  get #localitySelect() { return this.querySelector('[data-gd-select="locality"]'); }
  get #localityField() { return this.querySelector('[data-gd-locality-field]'); }
  get #result() { return this.querySelector('[data-gd-result]'); }

  // ── Data loading ──────────────────────────────────────────────────────────

  async #loadLocations() {
    // El CACHÉ es una optimización, NUNCA un punto de falla: si no se puede leer o viene
    // corrupto, `parsed` queda null y se cae al fetch, que es la fuente de verdad. Antes un
    // localStorage denegado lanzaba acá dentro y el catch de abajo mostraba "no se pudieron
    // cargar las regiones" SIN HABER INTENTADO el fetch (Sentry, 20-ago-2026).
    let parsed = null;
    try {
      const cached = lsGet(LS_LOCATIONS);
      parsed = cached ? JSON.parse(cached) : null;
    } catch {
      parsed = null;
    }

    try {
      if (parsed && parsed.v === LOCATIONS_VERSION) {
        this.#regions = parsed.regions;
      } else {
        const res = await fetch(`${this.#connectorBase}/shipping/locations`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        this.#regions = data.regions;
        lsSet(LS_LOCATIONS, JSON.stringify({ v: LOCATIONS_VERSION, regions: data.regions }));
      }
      this.#populateRegions();
    } catch (err) {
      console.error('[gd-shipping] Error cargando regiones:', err);
      this.#setResult('error', 'No se pudieron cargar las regiones. Intenta nuevamente.');
    }
  }

  #populateRegions() {
    const sel = this.#regionSelect;
    sel.innerHTML = '<option value="">Elige tu región</option>';
    for (const r of this.#regions) {
      const opt = document.createElement('option');
      opt.value = r.regionCode;
      opt.textContent = r.regionName;
      sel.appendChild(opt);
    }
    sel.disabled = false;

    if (this.#state.regionCode) {
      sel.value = this.#state.regionCode;
      this.#onRegionChange(false);
      if (this.#state.regionCode && this.#state.communeName && this.#state.localityCode) {
        this.#fetchQuote();
      }
    }
  }

  #onRegionChange(autoQuote = true) {
    const regionCode = this.#regionSelect.value;
    // Solo limpiar sub-selecciones si el usuario cambió la región manualmente.
    // Durante la restauración (autoQuote=false), preservar communeName/localityCode
    // que vienen del localStorage para que #onCommuneChange los use.
    if (this.#state.regionCode !== regionCode) {
      this.#state.communeName = '';
      this.#state.localityCode = '';
      this.#state.localityLabel = '';
    }
    this.#state.regionCode = regionCode;
    this.#clearResult();

    const communeSel = this.#communeSelect;
    communeSel.innerHTML = '<option value="">Elige tu comuna</option>';

    if (!regionCode) {
      communeSel.disabled = true;
      this.#hideLocality();
      return;
    }

    const region = this.#regions.find((r) => r.regionCode === regionCode);
    if (!region) return;

    for (const c of region.communes) {
      const opt = document.createElement('option');
      opt.value = c.commune;
      opt.textContent = c.commune;
      communeSel.appendChild(opt);
    }
    communeSel.disabled = false;

    if (this.#state.communeName) {
      communeSel.value = this.#state.communeName;
      this.#onCommuneChange(autoQuote);
    } else if (autoQuote) {
      // Cambio hecho por el usuario y la región nueva no tiene comuna elegida → el siguiente
      // paso es la comuna: llevarlo ahí sin que tenga que buscarla.
      this.#enfocarSiguiente(communeSel);
    }
  }

  #onCommuneChange(autoQuote = true) {
    const communeName = this.#communeSelect.value;
    // Solo limpiar locality si el usuario cambió la comuna manualmente.
    // Durante restauración preservar localityCode/localityLabel del localStorage.
    if (this.#state.communeName !== communeName) {
      this.#state.localityCode = '';
      this.#state.localityLabel = '';
    }
    this.#state.communeName = communeName;
    this.#clearResult();
    this.#hideLocality();

    if (!communeName) return;

    const region = this.#regions.find((r) => r.regionCode === this.#state.regionCode);
    const commune = region?.communes.find((c) => c.commune === communeName);
    if (!commune) return;

    const localities = commune.localities ?? [];

    const trivial = localities.length === 1 && localities[0].label === communeName;

    if (trivial) {
      // Comuna con una sola zona que se llama igual → auto-fill sin molestar al usuario
      this.#state.localityCode = localities[0].code;
      this.#state.localityLabel = localities[0].label;
      if (autoQuote) {
        // La zona quedó completa con este gesto → cuando llegue la tarifa, dejar PAGAR a la vista.
        this.#pedirRevelarCheckout();
        this.#fetchQuote();
      }
    } else {
      // 1 zona con nombre distinto a la comuna, o 2+ zonas → dropdown para que el usuario elija
      const localitySel = this.#localitySelect;
      localitySel.innerHTML = '<option value="">Elige tu localidad</option>';
      for (const l of localities) {
        const opt = document.createElement('option');
        opt.value = l.code;
        opt.textContent = l.label;
        localitySel.appendChild(opt);
      }
      localitySel.disabled = false;
      this.#localityField.hidden = false;

      if (this.#state.localityCode) {
        localitySel.value = this.#state.localityCode;
        if (autoQuote) {
          this.#pedirRevelarCheckout();
          this.#fetchQuote();
        }
      } else if (autoQuote) {
        // Falta la localidad → es el siguiente paso; el autoscroll a PAGAR viene después de ella.
        this.#enfocarSiguiente(localitySel);
      }
    }
  }

  #onLocalityChange() {
    const sel = this.#localitySelect;
    this.#state.localityCode = sel.value;
    this.#state.localityLabel = sel.selectedIndex >= 0 ? sel.options[sel.selectedIndex].textContent : '';
    this.#clearResult();
    if (this.#state.localityCode) {
      // Solo el usuario llega acá (no hay restauración por este camino) → zona completa.
      this.#pedirRevelarCheckout();
      this.#fetchQuote();
    }
  }

  // ── Encadenado de foco + revelar PAGAR (ver constantes arriba) ────────────

  // Lleva al usuario al siguiente select. Foco siempre; además abre el desplegable donde el
  // navegador lo permite (menos un toque en el flujo de 6 pasos). Se salta si el gesto fue
  // de teclado o si el select no está listo. Nada de esto puede romper la cotización: si algo
  // lanza, el cliente sigue con el flujo manual de siempre.
  #enfocarSiguiente(sel) {
    if (!sel || sel.disabled || this.#viaTeclado) return;
    try { sel.focus(); } catch { /* noop */ }
    if (!ABRIR_SIGUIENTE_SELECT || typeof sel.showPicker !== 'function') return;
    try { sel.showPicker(); } catch { /* sin activación de usuario o no renderizado → queda el foco */ }
  }

  // Marca que la PRÓXIMA cotización que termine de renderizar debe dejar PAGAR en viewport.
  // Se consume en #revelarCheckout (una sola vez) y expira solo (REVELAR_CHECKOUT_VENTANA_MS).
  #pedirRevelarCheckout() {
    if (this.#mode !== 'cart') return; // en PDP no hay PAGAR: el resultado aparece bajo los selects
    this.#revelarCheckoutHasta = Date.now() + REVELAR_CHECKOUT_VENTANA_MS;
  }

  // Autoscroll "al final" para que el botón PAGAR quede a la vista, con las tarifas justo
  // arriba. En el DRAWER el contenedor con scroll es `.cart-drawer__content` (items + resumen
  // juntos; Horizon lo hace scrollear entero cuando el resumen es alto, y el nuestro lo es) →
  // ir al fondo ES mostrar PAGAR. En la página /cart el que scrollea es el viewport y la página
  // sigue (recomendaciones, footer) → alinear el botón al borde inferior en vez de ir al fondo.
  // Se ejecuta DESPUÉS de renderizar el resultado (tarifas o error): las tarifas llegan async y
  // el cliente tiene que ver el precio Y el botón en el mismo cuadro.
  #revelarCheckout() {
    if (!this.#revelarCheckoutHasta) return;
    const vigente = Date.now() <= this.#revelarCheckoutHasta;
    this.#revelarCheckoutHasta = 0;
    if (!vigente || this.#mode !== 'cart') return;

    const btn = this.#checkoutButton();
    if (!btn) return;

    let behavior = 'smooth';
    try { if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'auto'; } catch { /* noop */ }

    const scroller = this.#contenedorConScroll(btn);
    try {
      if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior });
      else btn.scrollIntoView({ behavior, block: 'end' });
    } catch { /* noop */ }

    // MEDICIÓN (pedida por marketing): ¿el autoscroll dejó PAGAR a la vista de verdad? Hoy se
    // conoce `gd_shipping_cotizado` (486 en 1-20 sep) y `gd_checkout_iniciado` (23); con este
    // evento intermedio se distingue "no vio el botón" de "lo vio y no se decidió". Se mide
    // cuando el scroll suave ya terminó. `visible:false` con volumen = el scroll no alcanza.
    setTimeout(() => {
      try {
        track('gd_checkout_visible', {
          visible: this.#estaEnViewport(btn, scroller),
          via: 'autoscroll',
          superficie: this.closest('dialog') ? 'drawer' : 'pagina',
          comuna: this.#state.communeName || '(sin zona)',
          valor_carro: this.#cartTotalPesos(),
        });
      } catch { /* la medición jamás bloquea la compra */ }
    }, 800);
  }

  // Ancestro con scroll vertical real. Se excluyen <html>/<body> a propósito: un
  // `overflow-x: hidden` en body hace que overflow-y COMPUTE `auto` sin que body scrollee de
  // verdad (el scroll es del viewport) → ese caso se resuelve con scrollIntoView, no acá.
  #contenedorConScroll(el) {
    let node = el?.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      try {
        const { overflowY } = getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
      } catch { return null; }
      node = node.parentElement;
    }
    return null;
  }

  #estaEnViewport(el, scroller) {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    let top = 0;
    let bottom = vh;
    if (scroller) {
      const s = scroller.getBoundingClientRect();
      top = Math.max(top, s.top);
      bottom = Math.min(bottom, s.bottom);
    }
    return r.height > 0 && r.top >= top - 1 && r.bottom <= bottom + 1;
  }

  #hideLocality() {
    const field = this.#localityField;
    field.hidden = true;
    const sel = this.#localitySelect;
    sel.innerHTML = '<option value="">Elige una comuna primero</option>';
    sel.disabled = true;
  }

  // ── Quote ─────────────────────────────────────────────────────────────────

  // Mensajes por reason del connector (noCoverage legítimo — no reintentar)
  static #NO_COVERAGE_MESSAGES = {
    NO_COURIER_COVERAGE: 'No tenemos couriers para esta zona aún.',
    INVALID_LOCATION:    'Esta dirección no está en nuestra cobertura.',
    ORPHAN_ITEM:         'Un producto tiene un problema de catálogo. Contáctanos.',
    MISSING_ITEM_DIMS:   'Un producto aún no está listo para envío. Contáctanos.',
  };

  async #fetchQuote(opts) {
    try {
      await this.#fetchQuoteInner(opts);
    } finally {
      // Con o sin tarifa (error, sin cobertura), el resultado YA está pintado en [data-gd-result],
      // que vive justo arriba de PAGAR → revelar el botón muestra también el motivo. No-op si
      // nadie lo pidió (#pedirRevelarCheckout) o si se venció la ventana.
      this.#revelarCheckout();
    }
  }

  async #fetchQuoteInner({ retry = true } = {}) {
    const { regionCode, communeName, localityCode } = this.#state;
    if (!regionCode || !communeName || !localityCode) return;

    // En PDP la cantidad se resuelve SIEMPRE en el momento de cotizar leyendo el
    // selector de Horizon en vivo: única fuente de verdad, inmune a desincronización
    // (cambios con el disclosure cerrado, reflows del swap, etc.). En cart la cantidad
    // por línea viene de /cart.js (#refreshCartItems), que ya es la fuente real.
    const pdpQty = this.#mode === 'pdp' ? this.#readPdpQuantity() : null;
    const items = this.#items.map((i) => ({
      shopifyVariantGid: `gid://shopify/ProductVariant/${i.variantId}`,
      quantity: pdpQty ?? i.quantity,
    }));

    this.#setResult('loading', 'Calculando envío…');
    this.#setCheckoutBlocked(false);
    this.#saveDestination();

    let body = null;
    try {
      const res = await fetch(`${this.#connectorBase}/shipping/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regionCode, localityCode, items }),
        signal: timeoutSignal(10_000),
      });

      // Intentar parsear JSON sin asumir shape — puede ser HTML de Cloudflare en error
      try { body = await res.json(); } catch { /* no era JSON */ }

      if (!res.ok) {
        if (body?.error === 'UPSTREAM_FAILURE' && retry) {
          // Error transitorio del connector — retry único con backoff
          await new Promise((r) => setTimeout(r, 700));
          return this.#fetchQuoteInner({ retry: false });
        }
        // Otro error (502 de Cloudflare, 404, etc.) — sin retry
        console.error('[gd-shipping] Error al cotizar:', res.status, body);
        this.#setResult('error', 'Error al calcular envío. Intenta de nuevo.');
        this.#setCheckoutBlocked(true);
        // EVENTO 3a — el cliente quedó bloqueado por una FALLA NUESTRA, no por su zona.
        // Se separa de `sin_cobertura` a propósito: esto es una venta perdida por un bug
        // de infraestructura y se arregla arreglando el connector, no cambiando couriers.
        track('gd_shipping_error', {
          motivo: 'quote_fallido',
          detalle: `http_${res.status}`,
          region: regionCode,
          comuna: communeName,
        });
        return;
      }

      // Respuesta 200 — evaluar resultado
      if (body?.noCoverage) {
        const msg = GdShippingSelector.#NO_COVERAGE_MESSAGES[body.reason]
          ?? 'No hay envío disponible a esta zona.';
        this.#setResult('error', msg);
        this.#setCheckoutBlocked(true);
        // EVENTO 3b — SIN COBERTURA. El más accionable de todos: si aparece concentrado en
        // pocas comunas, son ventas que se recuperan sumando un courier o cotizando a mano,
        // y se puede estimar cuánta plata vale hacerlo.
        track('gd_shipping_sin_cobertura', {
          motivo: body.reason || 'desconocido',
          region: regionCode,
          comuna: communeName,
          localidad: localityCode,
        });
        return;
      }

      if (!body?.rates?.length) {
        this.#setResult('error', 'No hay envío disponible a esta zona. Contáctanos.');
        this.#setCheckoutBlocked(true);
        track('gd_shipping_sin_cobertura', {
          motivo: 'sin_tarifas',
          region: regionCode,
          comuna: communeName,
          localidad: localityCode,
        });
        return;
      }

      this.#setRates(body.rates);
      this.#setCheckoutBlocked(false);

      // EVENTO 2 — COTIZÓ BIEN, y CUÁNTO le salió. Es el dato que hoy no existe en ningún
      // lado: sabemos que el margen de envío es negativo, pero no qué monto ve el cliente
      // antes de irse. Con esto se puede cruzar el costo mostrado contra si terminó
      // comprando, y responder si el flete espanta o no (hoy es pura especulación).
      // ⚠️ El campo es `totalPriceClp`, NO `price` — y es **int CLP directo, no dividir por 100**
      // (la conversión a sub-unidad la hace el connector solo al responder el callback de
      // Shopify, en `shopify-rate.mapper.ts`; este endpoint entrega pesos).
      // Contrato: `docs/connector/fase3-carrier-service.md` + `carrier.types.ts:165`, y es el
      // mismo campo que usa `#setRates()` más abajo para pintar el precio.
      // Estuvo como `r.price` del 1 al 2-sep-2026 → `Number(undefined)||0` → el filtro
      // descartaba todo → `envio_min`/`envio_max` llegaban SIEMPRE en 0 a GA4 (14 eventos
      // perdidos). Falló en silencio: el evento se veía sano, solo el dato central iba vacío.
      const montos = body.rates.map((r) => Number(r.totalPriceClp) || 0).filter((n) => n > 0);
      track('gd_shipping_cotizado', {
        region: regionCode,
        comuna: communeName,
        localidad: localityCode,
        // el mínimo es lo que realmente pesa en la decisión: es la opción que va a elegir
        envio_min: montos.length ? Math.min(...montos) : 0,
        envio_max: montos.length ? Math.max(...montos) : 0,
        opciones: body.rates.length,
        // valor del carro en el momento de cotizar → permite leer el flete como % del ticket,
        // que es como lo percibe el cliente ($12.000 sobre $40.000 duele; sobre $300.000 no)
        valor_carro: this.#cartTotalPesos(),
        contexto: this.#mode,
      });
      // A Meta también: "cotizó el envío" es intención alta y sirve de público. Solo en
      // cart (en PDP cotizar es exploratorio, no decisión de compra).
      if (this.#mode === 'cart') {
        meta('CotizoEnvio', {
          comuna: communeName,
          envio: montos.length ? Math.min(...montos) : 0,
          value: this.#cartTotalPesos(),
          currency: 'CLP',
        });
      }
    } catch (err) {
      // GALPÓN (7-sep-2026) — UN reintento automático ante fallo de RED (no ante timeout: ya
      // esperó 10 s). CASO REAL: sábado 6-sep 20h, Instagram/Android, Concepción — 2 fallos de red
      // seguidos en la primera carga de la PDP ("Error al calcular envío. Intenta de nuevo."), y
      // al recargar cotizó 4 veces sin problema. El primer fetch a connector.* desde una red móvil
      // puede fallar (conexión fría); decirle "intenta de nuevo" es trasladarle nuestro problema.
      // Un reintento a 1,5 s la habría salvado. Se mide aparte (`gd_shipping_reintento`) para
      // saber cuán seguido pasa. `retry` es el mismo flag del reintento por UPSTREAM_FAILURE →
      // máximo 2 intentos en total, en cualquier combinación.
      if (retry && !esTimeout(err)) {
        track('gd_shipping_reintento', { detalle: 'red', region: regionCode, comuna: communeName });
        await new Promise((r) => setTimeout(r, 1500));
        return this.#fetchQuoteInner({ retry: false });
      }
      // network failure (ya reintentado) o timeout (connector completamente caído)
      console.error('[gd-shipping] Error de red al cotizar:', err);
      this.#setResult('error', 'Error al calcular envío. Intenta de nuevo.');
      this.#setCheckoutBlocked(true);
      track('gd_shipping_error', {
        motivo: 'quote_fallido',
        detalle: esTimeout(err) ? 'timeout' : 'red',
        region: regionCode,
        comuna: communeName,
      });
    }
  }

  // Valor del carro en pesos, para leer el flete como proporción del ticket. Solo tiene
  // sentido en cart (en PDP el "carro" es el producto que está mirando). Devuelve 0 si no
  // se puede determinar — nunca lanza, la medición no bloquea la venta.
  // ⚠️ `window.Shopify.cart` NO EXISTE en Horizon (verificado en producción 1-sep-2026).
  // Dos fuentes, en orden: (1) el `data-cart-total` que Liquid escribe al renderizar —
  // disponible YA al montar, sin ninguna request; (2) `/cart.js` vía #refreshCartItems,
  // que lo refresca cuando el cliente modifica el carro.
  // ⚠️ La (1) es imprescindible: #refreshCartItems SOLO corre con `cart:update`, o sea
  // cuando el carro CAMBIA. El caso más común —llegar al /cart con productos y clickear
  // PAGAR sin tocar nada— nunca dispara ese evento, y sin (1) el total iba en 0 justo en
  // los eventos que más importan (`gd_checkout_bloqueado`, `gd_checkout_iniciado`).
  // Síncrono a propósito: se llama desde el camino de un evento y no puede esperar red.
  #cartTotalCache = 0;

  #cartTotalPesos() {
    return this.#cartTotalCache;
  }

  #checkoutBlocked = false;

  #setCheckoutBlocked(blocked) {
    this.#checkoutBlocked = blocked;
    this.#syncCheckoutButton();
  }

  // Refleja #mustBlockCheckout() en el estado visual del botón PAGAR: se ve deshabilitado
  // mientras falte zona (envío) o el quote esté fallado → feedback INMEDIATO, el cliente
  // ve que no puede avanzar sin completar. La garantía dura está en el handler de
  // click/submit (capture:true). Solo en modo cart (en PDP no hay checkout).
  //
  // ⛔ NO usar `btn.disabled = true` acá (se usó hasta el 1-sep-2026 y era un BUG):
  // un `<button disabled>` NO despacha NINGÚN evento — ni click, ni pointerdown, ni
  // touchstart, ni siquiera en fase de captura sobre document. Medido en producción con
  // un click de mouse real: cero eventos. Consecuencia: `#promptForZone()` nunca corría,
  // y el cliente que clickeaba PAGAR no veía NADA — sin mensaje, sin scroll al select,
  // sin foco. El `title` solo aparece con hover, o sea en móvil no existe. Carrito lleno,
  // botón muerto, se va.
  // `aria-disabled` da el MISMO aspecto (base.css estila `.button[aria-disabled='true']`
  // igual que `:disabled`: opacity .5 + cursor not-allowed) pero deja pasar los eventos,
  // así el bloqueo duro sigue intacto Y además explica el motivo.
  // (`#setCheckoutButtonLoading` sí usa `disabled` a propósito: ahí queremos matar el
  // doble-click durante el fetch, y dura ~1s.)
  #syncCheckoutButton() {
    if (this.#mode !== 'cart') return;
    const block = this.#mustBlockCheckout();
    const btn = this.#checkoutButton();
    if (!btn) return;
    if (btn.disabled) return; // en loading manda #setCheckoutButtonLoading, no pisarlo
    btn.setAttribute('aria-disabled', block ? 'true' : 'false');
    btn.title = block ? 'Elige tu comuna o el retiro para continuar' : '';
  }

  // ── Checkout ──────────────────────────────────────────────────────────────

  // El botón PAGAR de ESTA instancia. En /cart conviven DOS selectores (el del drawer, en el
  // header, y el del resumen de la página) y `document.querySelector` devolvía siempre el
  // primero del DOM (el del drawer) → el PAGAR de la página nunca reflejaba el bloqueo. Se sube
  // por los ancestros hasta el primero que contenga un botón de checkout (el `.cart-summary__inner`
  // compartido por ambos contextos). Fallback al primero del documento si el markup cambiara.
  #checkoutButton() {
    let node = this.parentElement;
    while (node && node !== document.body) {
      const btn = node.querySelector('button[name="checkout"]');
      if (btn) return btn;
      node = node.parentElement;
    }
    return document.querySelector('button[name="checkout"]');
  }

  // Zona completa = región + comuna + localidad elegidas. SINE QUA NON para envío: el
  // checkout usa un campo de comuna de texto libre; si el cliente no fijó la zona acá, el
  // costo de envío no le llega. Por eso el checkout se BLOQUEA si es envío y falta zona.
  #hasCompleteZone() {
    const { regionCode, communeName, localityCode } = this.#state;
    return !!(regionCode && communeName && localityCode);
  }

  // ¿Hay que BLOQUEAR el checkout AHORA? Pickup nunca bloquea. Envío: bloquea si falta zona
  // (caso prohibido) o si el quote falló / sin cobertura.
  #mustBlockCheckout() {
    if (this.#activeTab === 'pickup') return false;
    if (!this.#hasCompleteZone()) return true;
    if (this.#checkoutBlocked) return true;
    return false;
  }

  // Feedback al bloquear: abrir el selector, mostrar el motivo y llevar el foco al primer
  // paso faltante, para que el cliente entienda QUÉ completar (no un "no pasa nada").
  #promptForZone() {
    // EVENTO 4 — el cliente INTENTÓ PAGAR y el sitio se lo impidió. Es la señal de intención
    // de compra más fuerte que existe fuera del checkout: carro armado y dedo en el botón.
    // ⚠️ Este evento era IMPOSIBLE de capturar hasta el fix del 1-sep-2026: el botón estaba
    // `disabled` y un <button disabled> no emite click, así que este método nunca corría.
    // Cualquier conteo de este evento arranca el 1-sep — no hay serie histórica con que
    // compararlo, y un cero antes de esa fecha significa "no medido", no "no pasaba".
    track('gd_checkout_bloqueado', {
      motivo: !this.#hasCompleteZone() ? 'falta_zona' : 'sin_cobertura_o_error',
      region: this.#state.regionCode || '(ninguna)',
      comuna: this.#state.communeName || '(ninguna)',
      valor_carro: this.#cartTotalPesos(),
    });
    // El público MÁS caliente que podemos armar: carro lleno + dedo en PAGAR + frenado.
    // Es gente que ya decidió comprar. A esos hay que volver a hablarles primero.
    meta('CheckoutBloqueado', {
      comuna: this.#state.communeName || '(ninguna)',
      value: this.#cartTotalPesos(),
      currency: 'CLP',
    });

    const details = this.querySelector('[data-gd-details]');
    if (details && !details.open) details.open = true;
    if (!this.#hasCompleteZone()) {
      this.#setResult('error', 'Elige tu comuna para calcular el despacho y continuar.');
      const target = !this.#state.regionCode ? this.#regionSelect
        : !this.#state.communeName ? this.#communeSelect
        : this.#localitySelect;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { target.focus({ preventScroll: true }); } catch { target.focus(); }
      }
    } else {
      // Zona completa pero bloqueado igual = el quote falló o no hay cobertura. El motivo
      // ya está escrito en [data-gd-result] por el flujo del quote; lo que falta es que el
      // cliente LO VEA — puede estar fuera de viewport tras scrollear hasta PAGAR.
      this.querySelector('[data-gd-result]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  #bindCheckoutButton() {
    const signal = this.#abortController.signal;

    // Interceptar el submit del form del carrito (página /cart completa).
    // El click en capture:true no alcanza el submit del <form> de Horizon.
    document.addEventListener('submit', (e) => {
      if (!this.isConnected) return;
      const form = e.target;
      if (form?.getAttribute('action') !== '/cart') return;
      if (this.#mustBlockCheckout()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.#promptForZone();
        return;
      }
    }, { capture: true, signal });

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button[name="checkout"]');
      if (!btn) return;
      if (!this.isConnected) return;

      // Bloqueo duro: envío sin zona (o quote fallado) NO puede ir al checkout.
      if (this.#mustBlockCheckout()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.#promptForZone();
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      // El pre-fill dispara 1-2 llamadas a la Storefront API antes de redirigir al
      // checkout (limpiar pickup + escribir dirección). Sin feedback, el usuario cree
      // que no pasó nada y re-clickea. Spinner + disable en el propio botón de Horizon:
      // se apaga solo si hay error; en éxito la página navega y no hace falta revertir.
      this.#setCheckoutButtonLoading(btn, true);

      // EVENTO 5 — CRUZÓ el selector y va camino al checkout. Es el DENOMINADOR que le
      // faltaba al embudo: `begin_checkout` de Shopify se dispara recién al CARGAR el
      // checkout, así que entre "clickeó PAGAR" y "llegó al checkout" había un tramo
      // invisible (pre-fill vía Storefront API, 1-2 llamadas de red que pueden fallar).
      // Con 4 y 5 juntos se sabe cuántos intentaron pagar y qué proporción pasó.
      track('gd_checkout_iniciado', {
        modo: this.#activeTab === 'pickup' ? 'retiro' : 'envio',
        region: this.#state.regionCode || '(retiro)',
        comuna: this.#state.communeName || '(retiro)',
        valor_carro: this.#cartTotalPesos(),
      });

      if (this.#activeTab === 'pickup') {
        this.#checkoutWithPickup(btn);
      } else {
        const { regionCode, communeName, localityCode } = this.#state;
        this.#checkoutWithShipping(regionCode, communeName, localityCode, btn);
      }
    }, { capture: true, signal });
  }

  // Activa/desactiva el estado de carga del botón nativo de checkout de Horizon.
  // Usa aria-busy + disabled (accesible) y una clase que pinta el spinner de marca
  // encima del texto (el texto se hace transparente vía CSS, no se reemplaza el DOM
  // del botón — así no rompe el markup de Horizon ni pierde el label al revertir).
  #setCheckoutButtonLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle('gd-checkout-btn--loading', on);
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.disabled = on;
    // Al salir del loading, devolver el botón al estado que corresponda según la zona
    // (si no, queda con el aria-disabled viejo o sin ninguno).
    if (!on) this.#syncCheckoutButton();
  }

  async #checkoutWithShipping(regionCode, communeName, localityCode, btn) {
    if (!this.#storefrontToken) {
      console.warn('[gd-shipping] storefront_api_token no configurado — checkout sin pre-fill de dirección');
      location.href = '/checkout';
      return;
    }

    try {
      const cartToken = await this.#getCartToken();
      const cartGid = `gid://shopify/Cart/${cartToken}`;

      // Primero limpiar preferencia de pickup que pueda haber quedado de una sesión anterior.
      // cartDeliveryAddressesReplace no pisa buyerIdentity.preferences, hay que hacerlo explícito.
      await this.#callStorefrontApi(`
        mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
          cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
            cart { id }
            userErrors { field message }
          }
        }
      `, {
        cartId: cartGid,
        buyerIdentity: {
          preferences: { delivery: { deliveryMethod: ['SHIPPING'] } },
        },
      });

      // localityCode va en zip: el carrier service lo lee como destination.postal_code
      // para cotizar la zona correcta (ver fase3-carrier-service.md).
      const deliveryAddress = {
        countryCode: 'CL',
        provinceCode: regionCode,
        city: communeName,
        zip: localityCode,
      };

      const data = await this.#callStorefrontApi(`
        mutation CartDeliveryAddressesReplace($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
          cartDeliveryAddressesReplace(cartId: $cartId, addresses: $addresses) {
            cart { id checkoutUrl }
            userErrors { field message }
          }
        }
      `, {
        cartId: cartGid,
        addresses: [{
          selected: true,
          oneTimeUse: true,
          address: { deliveryAddress },
        }],
      });

      const userErrors = data?.cartDeliveryAddressesReplace?.userErrors;
      if (userErrors?.length) {
        console.warn('[gd-shipping] cartDeliveryAddressesReplace userErrors:', userErrors);
        // EVENTO 6 — FALLO SILENCIOSO Y CARO: la dirección NO se escribió, pero igual
        // mandamos al cliente al checkout. Ahí le va a pedir la dirección de nuevo y puede
        // que NO le llegue la tarifa del carrier → ve "sin métodos de envío" o un costo
        // distinto al que le mostramos. Hasta hoy esto solo existía en la consola del
        // cliente, o sea: invisible. Si aparece con volumen, explica ventas perdidas
        // JUSTO en el último paso, que es el más caro de perder.
        track('gd_prefill_fallido', {
          detalle: String(userErrors[0]?.message || 'user_error').slice(0, 90),
          region: regionCode,
          comuna: communeName,
          valor_carro: this.#cartTotalPesos(),
        });
      }

      const checkoutUrl = data?.cartDeliveryAddressesReplace?.cart?.checkoutUrl;
      location.href = checkoutUrl || '/checkout';
    } catch (err) {
      console.error('[gd-shipping] Error en cartDeliveryAddressesReplace:', err);
      track('gd_prefill_fallido', {
        detalle: err?.name === 'TimeoutError' ? 'timeout' : String(err?.message || 'excepcion').slice(0, 90),
        region: regionCode,
        comuna: communeName,
        valor_carro: this.#cartTotalPesos(),
      });
      // Revertir el botón antes de redirigir: si el redirect fallara (bloqueo del
      // navegador, etc.), el botón no queda colgado en loading para siempre.
      this.#setCheckoutButtonLoading(btn, false);
      location.href = '/checkout';
    }
  }

  async #checkoutWithPickup(btn) {
    if (!this.#storefrontToken || !this.#pickupLocationGid) {
      console.warn('[gd-shipping] storefront_api_token o pickup_location_gid no configurados — checkout sin pre-selección de retiro');
      location.href = '/checkout';
      return;
    }

    try {
      const cartToken = await this.#getCartToken();
      const cartGid = `gid://shopify/Cart/${cartToken}`;

      const mutation = `
        mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
          cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
            cart { id checkoutUrl }
            userErrors { field message }
          }
        }
      `;

      const data = await this.#callStorefrontApi(mutation, {
        cartId: cartGid,
        buyerIdentity: {
          preferences: {
            delivery: {
              deliveryMethod: ['PICK_UP'],
              pickupHandle: [this.#pickupLocationGid],
            },
          },
        },
      });

      const userErrors = data?.cartBuyerIdentityUpdate?.userErrors;
      if (userErrors?.length) {
        console.warn('[gd-shipping] cartBuyerIdentityUpdate userErrors:', userErrors);
      }

      const checkoutUrl = data?.cartBuyerIdentityUpdate?.cart?.checkoutUrl;
      location.href = checkoutUrl || '/checkout';
    } catch (err) {
      console.error('[gd-shipping] Error en cartBuyerIdentityUpdate:', err);
      this.#setCheckoutButtonLoading(btn, false);
      location.href = '/checkout';
    }
  }

  async #callStorefrontApi(query, variables) {
    const shop = Shopify.shop;
    const res = await fetch(`https://${shop}/api/2025-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.#storefrontToken,
      },
      body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) {
      console.warn('[gd-shipping] Storefront API errors:', json.errors);
    }
    return json?.data;
  }

  async #getCartToken() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    return cart.token;
  }

  // ── Persistencia ──────────────────────────────────────────────────────────

  #saveDestination() {
    const { regionCode, communeName, localityCode, localityLabel } = this.#state;
    lsSet(
      LS_DESTINATION,
      JSON.stringify({ regionCode, communeName, localityCode, localityLabel, updatedAt: Date.now() })
    );
  }

  #restoreDestination() {
    try {
      const saved = lsGet(LS_DESTINATION);
      if (!saved) return;
      const { regionCode, communeName, localityCode, localityLabel } = JSON.parse(saved);
      if (regionCode) this.#state.regionCode = regionCode;
      if (communeName) this.#state.communeName = communeName;
      if (localityCode) this.#state.localityCode = localityCode;
      if (localityLabel) this.#state.localityLabel = localityLabel;
    } catch {
      // ignorar
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  #setResult(type, text) {
    const el = this.#result;
    el.className = `gd-shipping-selector__result gd-shipping-selector__result--${type}`;
    el.textContent = '';
    if (type === 'loading') {
      // Spinner sutil de marca + texto → feedback claro de "recalculando" (no un salto mágico).
      const spinner = document.createElement('span');
      spinner.className = 'gd-shipping-selector__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      el.append(spinner, document.createTextNode(text));
    } else {
      el.textContent = text;
    }
  }

  // Tarifas: una fila por courier (nombre sutil · precio destacado, alineados). Solo el
  // dato — sin "Despacho a", sin plazos: la elección de courier y sus condiciones van en
  // el checkout. Premium = restraint. Construido con nodos (no innerHTML) para no inyectar
  // el serviceName del connector como HTML.
  #setRates(rates) {
    const el = this.#result;
    el.className = 'gd-shipping-selector__result gd-shipping-selector__result--rates';
    el.textContent = '';

    // Más barata primero (la conveniente arriba). Solo marcamos --best cuando hay 2+ y
    // hay una claramente más barata: con una sola opción no tiene sentido "destacarla".
    const sorted = [...rates].sort((a, b) => a.totalPriceClp - b.totalPriceClp);
    const markBest = sorted.length > 1 && sorted[0].totalPriceClp < sorted[1].totalPriceClp;

    const list = document.createElement('ul');
    list.className = 'gd-shipping-selector__rates';
    sorted.forEach((r, i) => {
      const row = document.createElement('li');
      row.className = 'gd-shipping-selector__rate';
      if (markBest && i === 0) row.classList.add('gd-shipping-selector__rate--best');

      const name = document.createElement('span');
      name.className = 'gd-shipping-selector__rate-name';
      name.textContent = r.serviceName;

      const price = document.createElement('span');
      price.className = 'gd-shipping-selector__rate-price';
      price.textContent = this.#formatMoney(r.totalPriceClp);

      row.append(name, price);
      list.appendChild(row);
    });
    el.appendChild(list);

    // Con 2+ couriers, dos filas nombre·precio se leen como DESGLOSE que se suma (patrón
    // visual de subtotal/total). Micro-nota que implica alternativa ("una") y ubica la
    // elección en el flujo — sin explicar de más. Con 1 courier es ruido: no va.
    if (sorted.length > 1) {
      const note = document.createElement('p');
      note.className = 'gd-shipping-selector__rates-note';
      note.textContent = 'Elige una al pagar';
      el.appendChild(note);
    }
  }

  #clearResult() {
    const el = this.#result;
    el.className = 'gd-shipping-selector__result';
    el.textContent = '';
    // Se llama en cada cambio de region/comuna/localidad → punto único para re-evaluar el
    // botón PAGAR (queda bloqueado mientras la zona esté incompleta). No-op en PDP.
    this.#syncCheckoutButton();
  }

  #formatMoney(pesos) {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(pesos);
  }

  // ── Events ────────────────────────────────────────────────────────────────

  #bindEvents() {
    const signal = this.#abortController.signal;
    this.#regionSelect.addEventListener('change', () => this.#onRegionChange(), { signal });
    this.#communeSelect.addEventListener('change', () => this.#onCommuneChange(), { signal });
    this.#localitySelect.addEventListener('change', () => this.#onLocalityChange(), { signal });
    // Para el encadenado de foco: distinguir si el `change` vino del teclado o de un toque/clic.
    // `pointerdown` cubre mouse y touch (iOS 13+). El último gesto antes del `change` manda.
    for (const sel of [this.#regionSelect, this.#communeSelect, this.#localitySelect]) {
      sel.addEventListener('keydown', () => { this.#viaTeclado = true; }, { signal });
      sel.addEventListener('pointerdown', () => { this.#viaTeclado = false; }, { signal });
    }

    if (this.#mode === 'pdp') {
      // En la PDP el swap de familia es client-side: cuando el usuario cambia de
      // versión (otro Product = otra pieza con su propio costo de envío) gd-pdp.js
      // emite este evento con el variantId nuevo. Re-cotizamos con el mismo destino,
      // conservando la cantidad elegida (la leemos del input de Horizon, no la
      // reseteamos a 1 — el envío de 3 piezas cuesta ~3× el de 1).
      document.addEventListener('gd:pdp-variant-change', (e) => {
        const variantId = e.detail?.variantId;
        if (!variantId) return;
        // Solo el variantId cambia acá; la cantidad la lee #fetchQuote del DOM en vivo.
        this.#items = [{ variantId: Number(variantId), quantity: this.#readPdpQuantity() }];
        this.#clearResult();
        if (this.#state.regionCode && this.#state.communeName && this.#state.localityCode) {
          this.#fetchQuote();
        }
      }, { signal });

      // El selector de cantidad de Horizon emite 'quantity-selector:update' (bubbles)
      // al cambiar por botones ± o input. Re-cotizamos: #fetchQuote relee la cantidad
      // del DOM, así que solo hace falta disparar. (En single-stock el selector está
      // oculto → no llega este evento; la cantidad efectiva queda en 1, correcto.)
      document.addEventListener('quantity-selector:update', () => {
        if (!this.#items[0]?.variantId) return;
        if (this.#state.regionCode && this.#state.communeName && this.#state.localityCode) {
          this.#fetchQuote();
        }
      }, { signal });
    } else {
      this.#bindTabs();
      this.#bindCheckoutButton();
      // Feedback INMEDIATO al tocar ±: spinner ya, así no hay ~1.7s muertos (lo que tarda
      // Horizon en persistir y emitir cart:update) mostrando la tarifa vieja sin avisar.
      // PERO con red de seguridad: quantity-selector:update es optimista y puede NO terminar
      // en cart:update (ej. pedir más que el stock → Horizon rechaza, no persiste). El timeout
      // saca el spinner si la recotización no llegó → nunca queda colgado en "Calculando…".
      document.addEventListener('quantity-selector:update', () => {
        if (this.#activeTab === 'pickup' || !this.#hasCompleteZone()) return;
        this.#setResult('loading', 'Calculando envío…');
        clearTimeout(this.#spinnerTimeout);
        this.#spinnerTimeout = setTimeout(() => {
          // Si seguimos en "Calculando…" tras el margen, no llegó cart:update (cambio
          // rechazado) → restaurar las tarifas vigentes (la cantidad real no cambió).
          if (this.#result?.classList.contains('gd-shipping-selector__result--loading')) {
            this.#fetchQuote();
          }
        }, 3000);
      }, { signal });

      // Recotización REAL: cart:update llega cuando Horizon ya persistió → /cart.js trae lo real.
      document.addEventListener('cart:update', () => {
        clearTimeout(this.#spinnerTimeout); // llegó el cambio real → cancelar la red de seguridad
        this.#refreshCartItems();
      }, { signal });
    }
  }

  #spinnerTimeout = null;

  // Lee la cantidad elegida en el selector de cantidad de Horizon en la PDP.
  // Fuente de verdad en vivo (no estado paralelo que se desincronice en el swap):
  // si el input no existe o está oculto (single-stock), la cantidad es 1.
  #readPdpQuantity() {
    const input = document.querySelector('product-form-component input[name="quantity"]');
    if (!input || input.disabled || input.offsetParent === null) return 1;
    const qty = parseInt(input.value, 10);
    return Number.isFinite(qty) && qty >= 1 ? qty : 1;
  }

  async #refreshCartItems() {
    if (this.#mode !== 'cart') return;
    try {
      const cart = await (await fetch('/cart.js')).json();
      this.#items = (cart.items || []).map((i) => ({ variantId: i.variant_id, quantity: i.quantity }));
      // cachear el total para la instrumentación (total_price viene en centavos)
      if (typeof cart.total_price === 'number') this.#cartTotalCache = Math.round(cart.total_price / 100);
      if (!this.#items.length) { this.#clearResult(); return; }
      if (this.#activeTab !== 'pickup' && this.#hasCompleteZone()) {
        this.#fetchQuote();
      }
      // Horizon recrea el botón PAGAR al re-renderizar el drawer → re-aplicar su estado.
      this.#syncCheckoutButton();
    } catch (err) {
      console.error('[gd-shipping] Error refrescando items del carrito:', err);
    }
  }
}

customElements.define('gd-shipping-selector', GdShippingSelector);
